import { Big } from 'big.js';
import { bigToString, Corridor, CorridorLevel, EnumCorridorColor, getCorridorColorFromString, parseBig } from 'quote-domain-core';
import { CorridorLevelService } from './corridor-level-service';
import { singleton } from 'tsyringe';

@singleton()
export class CorridorService {
    constructor(private readonly corridorLevelService: CorridorLevelService) {
    }

    public getHighestLevel(corridor: Corridor): CorridorLevel {
        return corridor.levels.get(EnumCorridorColor.GREEN);
    }

    public getLowestLevel(corridor: Corridor): CorridorLevel {
        return corridor.levels.get(EnumCorridorColor.BLACK);
    }

    public getR7090(corridor: Corridor): string {
        return corridor.r7090?.toString();
    }

    public getR5090(corridor: Corridor): string {
        return corridor.r5090?.toString();
    }

    public getR3090(corridor: Corridor): string {
        return corridor.r3090?.toString();
    }

    public getR1090(corridor: Corridor): string {
        return corridor.r1090?.toString();
    }

    private computeRemise(corridor: Corridor, color: EnumCorridorColor): string {
        if (corridor.levels.size === 0) return '0';
        const green = corridor.levels.get(EnumCorridorColor.GREEN);
        if (!green) return '0';
        const greenMin = parseBig(green.min);
        const level = corridor.levels.get(color);
        if (!level || greenMin.eq(0)) return '0';
        return bigToString(new Big(1).minus(parseBig(level.min).div(greenMin)));
    }

    public addingPrice(corridor: Corridor, price: Big): void {
        if (!!price) {
            corridor.levels.forEach((level, key) => {
                if (level) {
                    if (key == EnumCorridorColor.BELOW_BLACK) {
                        this.corridorLevelService.addingPriceToMaxValue(level, price);
                    } else {
                        this.corridorLevelService.addingPrice(level, price);
                    }
                }
                corridor.levels.set(key, level);
            });
        }
    }

    public multiply(corridor: Corridor, value: Big): void {
        if (!!value) {
            corridor.levels.forEach((level, key) => {
                if (level) {
                    this.corridorLevelService.multiply(level, value);
                }
                corridor.levels.set(key, level);
            });
        }
    }

    public multiplyQuantity(corridor: Corridor, quantity: Big): Corridor {
        const result = new Corridor();
        corridor.levels.forEach((level, key) => {
            const newLevel = new CorridorLevel(level.max, level.min);
            this.corridorLevelService.multiplyQuantity(newLevel, quantity);
            result.levels.set(key, newLevel);
        });
        return result;
    }

    public addCorridor(corridor: Corridor, otherCorridor: Corridor): Corridor {
        if (!!otherCorridor) {
            if (otherCorridor.levels.size > 0 && corridor.levels.size > 0) {
                corridor.levels.forEach((level, key) => {
                    const max = otherCorridor.levels.get(key).max;
                    const min = otherCorridor.levels.get(key).min;
                    const newLevel = new CorridorLevel(level.max, level.min);
                    this.corridorLevelService.adding(newLevel, max, min);
                    corridor.levels.set(key, newLevel);
                });
            } else {
                otherCorridor.levels.forEach((level, key) => {
                    const max = otherCorridor.levels.get(key).max;
                    const min = otherCorridor.levels.get(key).min;
                    corridor.levels.set(key, new CorridorLevel(max, min));
                });
            }
        }
        return corridor;
    }

    public isFlat(corridor: Corridor): boolean {
        return this.getHighestLevel(corridor)?.min === this.getLowestLevel(corridor)?.min;
    }

    public getMatchedColorByappliedPrice(corridor: Corridor, appliedPrice: string, nonNegociableArticleColor: string): string {
        let matchedColor: string = EnumCorridorColor.GRAY;
        if (corridor.levels.size > 0) {
            if (this.isFlat(corridor)) {
                if (corridor.isNeutral) {
                    matchedColor = EnumCorridorColor.GRAY;
                } else {
                    matchedColor = getCorridorColorFromString(nonNegociableArticleColor);
                }
            } else {
                matchedColor = this.getMatchedColorByappliedPriceForNotFlatCorridor(corridor, appliedPrice);
            }
        }
        return matchedColor;
    }

    public getMatchedColorByappliedPriceForNotFlatCorridor(corridor: Corridor, appliedPrice: string) {
        if (!appliedPrice) {
            return EnumCorridorColor.GRAY;
        }
        const fixedAppliedPrice = new Big(new Big(appliedPrice).toFixed(3, 1));
        if (corridor.levels.size > 0) {
            for (const [key, level] of corridor.levels.entries()) {
                if (fixedAppliedPrice.gte(new Big(level.min).toFixed(3, 1)) && fixedAppliedPrice.lt(new Big(level.max))) {
                    return key;
                }
            }
        }
        return EnumCorridorColor.GRAY;
    }

    public getMatchedLevelByappliedPriceForNotFlatCorridor(corridor: Corridor, appliedPrice: string) {
        let matchedLevel = null;
        const fixedAppliedPrice = new Big(new Big(appliedPrice).toFixed(3, 1));
        if (corridor.levels.size > 0) {
            for (const [key, level] of corridor.levels.entries()) {
                if (fixedAppliedPrice.gte(new Big(level.min).toFixed(3, 1)) && fixedAppliedPrice.lt(new Big(level.max))) {
                    matchedLevel = level;
                    break;
                }
            }
        }
        return matchedLevel;
    }

    public getMatchedLevelByColor(corridor: Corridor, serviceColor: string): CorridorLevel {
        return corridor.levels.get(getCorridorColorFromString(serviceColor));
    }

    public getDistanceByAppliedPrice(corridor: Corridor, appliedPrice: string): Big {
        if (appliedPrice && !this.isFlat(corridor)) {
            const matchedLevel = this.getMatchedLevelByappliedPriceForNotFlatCorridor(corridor, appliedPrice);
            if (!!matchedLevel) {
                const fixedAppliedPrice = new Big(new Big(appliedPrice).toFixed(3, 1));
                const fixedMax = new Big(new Big(matchedLevel.max).toFixed(3, 1));
                const fixedMin = new Big(new Big(matchedLevel.min).toFixed(3, 1));
                return (fixedMax.minus(fixedAppliedPrice)).div(fixedMax.minus(fixedMin));
            }
        }
        return null;
    }
}
